import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { WorkerService } from 'src/app/data/worker.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { IPagedResult } from 'src/app/shared/models/PagedResult';

export class AssignEmployerObjectDataSource {
  private readonly rowsSubject = new BehaviorSubject<DictionaryItem[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly totalCountSubject = new BehaviorSubject<number>(0);
  private readonly cancellationSubject = new Subject<void>();

  private workerId = 0;
  private pageSize = 50;
  private hasMorePages = true;
  private currentPage = 0;
  private currentFilter = '';

  readonly rows$ = this.rowsSubject.asObservable();
  readonly isLoading$ = this.loadingSubject.asObservable();
  readonly totalCount$ = this.totalCountSubject.asObservable();

  get rows(): DictionaryItem[] {
    return this.rowsSubject.getValue();
  }

  get totalCount(): number {
    return this.totalCountSubject.getValue();
  }

  get isLoading(): boolean {
    return this.loadingSubject.getValue();
  }

  constructor(private readonly workerService: WorkerService) {}

  initialize(workerId: number, pageSize: number): void {
    this.workerId = workerId;
    this.pageSize = pageSize;
    this.currentFilter = '';
    this.refresh();
  }

  setFilter(name: string): void {
    const normalized = (name ?? '').trim();
    if (this.currentFilter === normalized) {
      return;
    }

    this.currentFilter = normalized;
    this.refresh();
  }

  refresh(): void {
    this.cancellationAndReset();
    this.fetchNextPage();
  }

  fetchNextPage(): void {
    if (this.isLoading || this.workerId === 0) {
      return;
    }

    if (!this.hasMorePages) {
      return;
    }

    const nextPage = this.currentPage + 1;
    this.loadingSubject.next(true);

    this.workerService
      .getInternalWorkerEmployerObjects(this.workerId, nextPage, this.pageSize, this.currentFilter)
      .pipe(
        takeUntil(this.cancellationSubject),
        finalize(() => this.loadingSubject.next(false)),
      )
      .subscribe({
        next: (response: IPagedResult<DictionaryItem>) => {
          this.currentPage = nextPage;
          const items = response?.Results ?? [];
          const updatedRows = nextPage === 1 ? items : [...this.rows, ...items];
          this.rowsSubject.next(updatedRows);

          this.hasMorePages = items.length === this.pageSize;
          this.totalCountSubject.next(response?.Count ?? updatedRows.length);
        },
        error: () => {
          // Keep current page and count unchanged when loading fails.
        },
      });
  }

  disconnect(): void {
    this.cancellationSubject.next();
    this.loadingSubject.next(false);
  }

  private cancellationAndReset(): void {
    this.cancellationSubject.next();
    this.loadingSubject.next(false);
    this.hasMorePages = true;
    this.currentPage = 0;
    this.rowsSubject.next([]);
    this.totalCountSubject.next(0);
  }
}
